/** Resolves the default fee amount for a member's next payment (Task 0.7). */
import type { GymSupabaseClient } from '@gym/api-client';

export interface MembershipFeeRow {
  status: string;
  end_date: string | null;
  plan_id: string;
  locked_renewal_price: number | null;
}

/**
 * Pure function: picks the member's "current" membership row.
 * Priority: active membership → most-recently-ended membership (null end_date is
 * treated as the MOST recent, i.e. open-ended). Returns null when there are none.
 */
export function pickCurrentMembership(memberships: MembershipFeeRow[]): MembershipFeeRow | null {
  if (memberships.length === 0) return null;
  const active = memberships.find((m) => m.status === 'active');
  // Fallback: most-recently-ended membership. A null end_date means open-ended,
  // so treat it as the MOST recent (sort key = far-future sentinel) rather than ''.
  const endKey = (m: MembershipFeeRow): string => m.end_date ?? '9999-12-31';
  return active ?? [...memberships].sort((a, b) => endKey(b).localeCompare(endKey(a)))[0] ?? null;
}

/**
 * Pure function: picks the best fee from a list of membership rows + a plan-price map.
 * Priority: active membership locked_renewal_price → active membership plan price →
 * most-recently-expired membership plan price → null.
 */
export function pickDefaultFee(
  memberships: MembershipFeeRow[],
  planPrice: Record<string, number>,
): number | null {
  const chosen = pickCurrentMembership(memberships);
  if (!chosen) return null;
  return chosen.locked_renewal_price ?? planPrice[chosen.plan_id] ?? null;
}

/**
 * Pure function: picks the member's preselected plan + amount from membership rows
 * and a plan-price map. Mirrors pickDefaultFee but also returns the plan id so a
 * payment register can preselect the member's current plan. Returns null when the
 * member has no memberships or the chosen membership lacks a plan id.
 */
export function pickDefaultPlan(
  memberships: MembershipFeeRow[],
  planPrice: Record<string, number>,
): { planId: string; amountMinor: number } | null {
  const chosen = pickCurrentMembership(memberships);
  if (!chosen?.plan_id) return null;
  const amountMinor = chosen.locked_renewal_price ?? planPrice[chosen.plan_id] ?? 0;
  return { planId: chosen.plan_id, amountMinor };
}

/**
 * Async helper: queries Supabase for the member's memberships + their plan prices,
 * then delegates to pickDefaultFee.
 */
export async function resolveDefaultFee(
  client: GymSupabaseClient,
  _tenantId: string,
  memberId: string,
): Promise<number | null> {
  const ms = await client
    .from('memberships')
    .select('status, end_date, plan_id, locked_renewal_price')
    .eq('member_id', memberId);
  const rows = (ms.data ?? []) as MembershipFeeRow[];
  if (rows.length === 0) return null;
  const planIds = [...new Set(rows.map((r) => r.plan_id))];
  const plans = await client.from('plans').select('id, price').in('id', planIds);
  const priceMap: Record<string, number> = {};
  for (const p of (plans.data ?? []) as { id: string; price: number }[]) {
    priceMap[p.id] = p.price;
  }
  return pickDefaultFee(rows, priceMap);
}

/**
 * Async helper: resolves the member's preselected plan + amount for the inline
 * payment register. Picks the member's active membership (else most-recently-ended,
 * treating a null end_date as the most recent) → its plan_id, and amount =
 * coalesce(locked_renewal_price, plans.price). Returns null when the member has no
 * usable membership.
 */
export async function resolveDefaultPlan(
  client: GymSupabaseClient,
  _tenantId: string,
  memberId: string,
): Promise<{ planId: string; amountMinor: number } | null> {
  const ms = await client
    .from('memberships')
    .select('status, end_date, plan_id, locked_renewal_price')
    .eq('member_id', memberId);
  const rows = (ms.data ?? []) as MembershipFeeRow[];
  if (rows.length === 0) return null;
  const planIds = [...new Set(rows.map((r) => r.plan_id))];
  const plans = await client.from('plans').select('id, price').in('id', planIds);
  const priceMap: Record<string, number> = {};
  for (const p of (plans.data ?? []) as { id: string; price: number }[]) {
    priceMap[p.id] = p.price;
  }
  return pickDefaultPlan(rows, priceMap);
}
